import React, { useState, useEffect, useCallback } from 'react';
import { Toaster } from 'react-hot-toast';
import OfficeList from './components/OfficeList';
import IPRangeList from './components/IPRangeList';
import PublicIPList from './components/PublicIPList';
import PublicIPForm from './components/PublicIPForm';
import { Office, PublicIP, officeApi, ipRangeApi, staticIpApi, dhcpIpApi } from './services/api';
import { useAuth } from './contexts/AuthContext';
import './App.css';

function App() {
  const { user, logout, canEdit, canAdmin } = useAuth();
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);
  const [selectedPublicIP, setSelectedPublicIP] = useState<PublicIP | null>(null);
  const [showPublicIPForm, setShowPublicIPForm] = useState(false);
  const [refreshPublicIPs, setRefreshPublicIPs] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [allOffices, setAllOffices] = useState<Office[]>([]);
  const [selectedRangeId, setSelectedRangeId] = useState<number | null>(null);


  const handlePublicIPFormSuccess = () => {
    setShowPublicIPForm(false);
    setSelectedPublicIP(null);
    setRefreshPublicIPs(prev => prev + 1);
  };

  const handlePublicIPFormCancel = () => {
    setShowPublicIPForm(false);
    setSelectedPublicIP(null);
  };

  const handleEditPublicIP = (publicIP: PublicIP) => {
    setSelectedPublicIP(publicIP);
    setShowPublicIPForm(true);
  };

  const handleAddPublicIP = () => {
    setSelectedPublicIP(null);
    setShowPublicIPForm(true);
  };

  // Load all offices on mount
  useEffect(() => {
    const loadOffices = async () => {
      try {
        const response = await officeApi.getAll();
        setAllOffices(response.data);
      } catch (error) {
        console.error('Failed to load offices:', error);
      }
    };
    loadOffices();
  }, []);

  // Search and auto-select when query changes
  useEffect(() => {
    const searchAndSelect = async () => {
      if (!searchQuery.trim()) return;

      try {
        const query = searchQuery.toLowerCase();
        
        // First, do a global DHCP search
        // Pass original searchQuery to API (API will handle normalization)
        const dhcpSearchResponse = await dhcpIpApi.search(searchQuery);
        
        console.log('DHCP search results:', dhcpSearchResponse.data.length, 'items');
        
        if (dhcpSearchResponse.data.length > 0) {
          const matchingDhcp = dhcpSearchResponse.data[0];
          console.log('First DHCP match:', matchingDhcp);
          
          // Find which range this IP belongs to
          for (const office of allOffices) {
            const rangesResponse = await ipRangeApi.getAll(office.id);
            
            for (const range of rangesResponse.data) {
              // Simple IP range check
              const ipParts = matchingDhcp.ip_address.split('.').map(Number);
              const netParts = range.network.split('.').map(Number);
              const mask = range.subnet_mask;
              
              let matches = true;
              
              // Check based on subnet mask
              if (mask >= 8) {
                if (ipParts[0] !== netParts[0]) matches = false;
              }
              if (mask >= 16 && matches) {
                if (ipParts[1] !== netParts[1]) matches = false;
              }
              if (mask >= 24 && matches) {
                if (ipParts[2] !== netParts[2]) matches = false;
              }
              
              // For masks not on byte boundaries
              if (matches && mask % 8 !== 0) {
                const byteIndex = Math.floor(mask / 8);
                const bitMask = (0xFF << (8 - (mask % 8))) & 0xFF;
                if ((ipParts[byteIndex] & bitMask) !== (netParts[byteIndex] & bitMask)) {
                  matches = false;
                }
              }
              
              if (matches) {
                // Fetch full office data and set both office and range
                const fullOfficeResponse = await officeApi.getById(office.id);
                const fullOffice = fullOfficeResponse.data;
                
                setSelectedOffice(fullOffice);
                setSelectedRangeId(range.id);
                
                return;
              }
            }
          }
          
          // No matching range found for IP
          return;
        }
        
        // If no DHCP match found, check IP ranges and static IPs across all offices
        for (const office of allOffices) {
          const rangesResponse = await ipRangeApi.getAll(office.id);
          
          for (const range of rangesResponse.data) {
            // First check if search matches the range itself
            const rangeNetwork = `${range.network}/${range.subnet_mask}`;
            if (range.network.includes(query) || 
                rangeNetwork.includes(query) ||
                range.vlan_id?.toString().includes(query) ||
                range.description?.toLowerCase().includes(query)) {
              // Found a matching range - select the office and range
              const fullOfficeResponse = await officeApi.getById(office.id);
              setSelectedOffice(fullOfficeResponse.data);
              setSelectedRangeId(range.id);
              return;
            }
            
            // Then check static IPs
            const staticIPsResponse = await staticIpApi.getAll(range.id);
            const matchingStatic = staticIPsResponse.data.find(ip => {
              // Check regular fields
              if (ip.ip_address.includes(query) ||
                  ip.device_name?.toLowerCase().includes(query) ||
                  ip.device_type?.toLowerCase().includes(query) ||
                  ip.description?.toLowerCase().includes(query)) {
                return true;
              }
              
              // Special handling for MAC address - normalize both query and stored MAC
              if (ip.mac_address) {
                const normalizedQuery = query.toUpperCase().replace(/[^0-9A-F]/g, '');
                const normalizedMac = ip.mac_address.toUpperCase().replace(/[^0-9A-F]/g, '');
                return normalizedMac.includes(normalizedQuery);
              }
              
              return false;
            });
            
            if (matchingStatic) {
              // Found a static match - select the office and range
              const fullOfficeResponse = await officeApi.getById(office.id);
              setSelectedOffice(fullOfficeResponse.data);
              // Set range ID immediately - IPRangeList will handle it when it loads
              setSelectedRangeId(range.id);
              return;
            }
          }
        }
      } catch (error) {
        console.error('Search error:', error);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(searchAndSelect, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, allOffices]);

  return (
    <div className="App">
      <Toaster position="top-right" />
      <header className="App-header">
        <div className="header-left">
          <h1>SHIFT UP IP Pool Management</h1>
        </div>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="IP 주소, MAC 주소, 호스트명, 장치명 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
        </div>
        <div className="header-right">
          {user && (
            <div className="user-info">
              {user.picture && (
                <img src={user.picture} alt={user.name} className="user-avatar" />
              )}
              <span className="user-name">{user.name}</span>
              <span className="user-role">({user.role.toUpperCase()})</span>
              {canAdmin() && (
                <a href="/admin" className="admin-link">관리자</a>
              )}
              <button onClick={logout} className="logout-btn">로그아웃</button>
            </div>
          )}
        </div>
      </header>
      
      <div className="App-content">
        <div className="offices-section">
          <OfficeList 
            onOfficeSelect={(office) => {
              setSelectedOffice(office);
              // Clear range selection when office changes
              if (office?.id !== selectedOffice?.id) {
                setSelectedRangeId(null);
              }
            }} 
            selectedOffice={selectedOffice}
          />
        </div>
        
        <div className="network-section">
          <div className="public-ips-section">
            <div className="section-header">
              <h2>Public IPs</h2>
              {selectedOffice && canEdit() && (
                <button onClick={handleAddPublicIP} className="btn btn-primary">
                  Add Public IP
                </button>
              )}
            </div>
            <PublicIPList 
              key={refreshPublicIPs}
              selectedOffice={selectedOffice} 
              onSelectPublicIP={handleEditPublicIP}
            />
          </div>
          
          <div className="ip-ranges-section">
            <IPRangeList 
              office={selectedOffice} 
              searchQuery={searchQuery}
              selectedRangeId={selectedRangeId}
              onRangeSelect={setSelectedRangeId}
            />
          </div>
        </div>
      </div>

      {showPublicIPForm && (
        <div className="modal-overlay">
          <PublicIPForm
            selectedOffice={selectedOffice}
            publicIP={selectedPublicIP}
            onSuccess={handlePublicIPFormSuccess}
            onCancel={handlePublicIPFormCancel}
          />
        </div>
      )}
    </div>
  );
}

export default App;